import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'

const initialState = {
  bankFeed: null,
  bankFeeds: [],
  bankFeedsHaveChanged: 0,
  currentLastSync: null,
  ospreyBankAccounts: [],
  paymentLink: null,
  transactions: [],
  unpaidBills: []
}

export const useBankFeedsStore = storeWrapper('useBankFeedsStore', (set) => ({
  ...initialState,
  setSingleBankFeed: async ({ bankFeed }) => {
    set({ bankFeed: bankFeed })
  },
  setBankFeedsHaveChanged: async () => {
    set((state) => ({ bankFeedsHaveChanged: state.bankFeedsHaveChanged + 1 }))
  },
  resetBankFeedsHaveChanged: async () => {
    set({ bankFeedsHaveChanged: 0 })
  },
  addOspreyBankAccountToList: async ({ ospreyBankAccount }) => {
    set((state) => ({
      ospreyBankAccounts: [...state.ospreyBankAccounts, ospreyBankAccount]
    }))
  },
  removeOspreyBankAccountFromList: async ({ ospreyBankAccountId }) => {
    set((state) => ({
      ospreyBankAccounts: state.ospreyBankAccounts.filter((account) => account.bankAccountId !== ospreyBankAccountId)
    }))
  },
  resetOspreyBankAccounts: async () => {
    set({ ospreyBankAccounts: [] })
  },
  fetchAllBankFeeds: async () => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchAllBankFeeds',
        delay: 300,
        callback: async () => {
          let allBankFeeds = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidBankAccount/GetAllExtended',
            method: 'GET'
          })

          set({ bankFeeds: allBankFeeds })
          resolve(allBankFeeds)
        }
      })
    })
  },
  fetchGeneratedLinkToken: async () => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchGeneratedLinkToken',
        delay: 300,
        callback: async () => {
          let linkToken = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidAuthentication/CreateLinkToken',
            method: 'GET'
          })

          resolve(linkToken)
        }
      })
    })
  },
  fetchOspreyBankAccounts: async () => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchOspreyBankAccounts',
        delay: 300,
        callback: async () => {
          let ospreyBankAccounts = await ospreyAPI({
            service: 'banking',
            endpoint: '/BankAccount/GetAll',
            method: 'GET'
          })

          set({ ospreyBankAccounts })
          resolve(ospreyBankAccounts)
        }
      })
    })
  },
  updateBankFeed: async ({ bankFeed }) => {
    return new Promise((resolve) => {
      debounce({
        id: `updateBankFeed-${bankFeed.plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          let response = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidBankAccount/Update',
            method: 'PUT',
            data: bankFeed
          })

          resolve(response)
        }
      })
    })
  },
  deleteBankFeed: async ({ plaidBankAccountId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `deleteBankFeed-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          let response = await ospreyAPI({
            service: 'banking',
            endpoint: `/PlaidBankAccount/Delete`,
            method: 'DELETE',
            sendAsUrlParams: true,
            suppressErrorToast: true,
            data: {
              id: plaidBankAccountId
            }
          })

          resolve(response)
        }
      })
    })
  },
  saveNewBankFeeds: async ({ institutionId, institutionName, plaidBankAccountMappings, publicToken }) => {
    return new Promise((resolve) => {
      debounce({
        id: 'saveNewBankFeeds',
        delay: 300,
        callback: async () => {
          let response = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidAuthentication/SavePublicTokens',
            method: 'POST',
            data: {
              institutionId: institutionId,
              institutionName: institutionName,
              plaidBankAccountMappings: plaidBankAccountMappings,
              publicToken: publicToken
            }
          })

          resolve(response)
        }
      })
    })
  },
  fetchUnpaidBillsByClientNo: async ({ clientNo }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchUnpaidBillsByClientNo-${clientNo}`,
        delay: 300,
        callback: async () => {
          let unpaidBills = await ospreyAPI({
            service: 'matters',
            endpoint: '/LedgerPosting/GetAllUnpaidLedgerPostingsByClientNo',
            method: 'GET',
            data: { clientNo: clientNo }
          })

          set({ unpaidBills: unpaidBills })
          resolve(unpaidBills)
        }
      })
    })
  },
  createPaymentLink: async ({ plaidBankAccountId, ledgerPostingId, paymentReference, value }) => {
    return new Promise((resolve) => {
      debounce({
        id: `createPaymentLink-${plaidBankAccountId}-${ledgerPostingId}`,
        delay: 300,
        callback: async () => {
          let paymentLink = await ospreyAPI({
            service: 'banking',
            endpoint: '/OspreyPaymentLink/CreatePaymentLink',
            method: 'POST',
            sendAsUrlParams: true,
            data: {
              plaidBankAccountId: plaidBankAccountId,
              ledgerPostingId: ledgerPostingId,
              paymentReference: paymentReference,
              value: value
            }
          })

          set({ paymentLink: paymentLink })
          resolve(paymentLink)
        }
      })
    })
  },
  fetchAllTransactionsForBankFeed: async ({ startAtIndex, pageSize, plaidBankAccountId, startDate, endDate }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllTransactionsForBankFeed-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          let transactions = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidBankAccount/GetAllTransactionsBySearchCriteria',
            method: 'POST',
            data: {
              startAtIndex: startAtIndex,
              pageSize: pageSize,
              plaidBankAccountId: plaidBankAccountId,
              startDate: startDate ?? null,
              endDate: endDate ?? null
            }
          })

          const lastSync = new Date().toISOString()

          set((state) => {
            const updatedBankFeeds = (state.bankFeeds || []).map((feed) =>
              feed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...feed,
                    lastSync: lastSync
                  }
                : feed
            )

            const updatedBankFeed =
              state.bankFeed && state.bankFeed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...state.bankFeed,
                    lastSync: lastSync
                  }
                : state.bankFeed

            return {
              bankFeeds: updatedBankFeeds,
              bankFeed: updatedBankFeed
            }
          })

          set({ transactions: transactions })
          resolve(transactions)
        }
      })
    })
  },
  fetchAllTransactionsForBankFeedAsCSV: async ({ startAtIndex, pageSize, plaidBankAccountId, startDate, endDate }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllTransactionsForBankFeedAsCSV-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          let transactionsCSV = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidBankAccount/GetAllTransactionsBySearchCriteriaAsCsv',
            method: 'POST',
            data: {
              startAtIndex: startAtIndex,
              pageSize: pageSize,
              plaidBankAccountId: plaidBankAccountId,
              startDate: startDate ?? null,
              endDate: endDate ?? null
            },
            successToast: {
              type: 'success',
              title: `Success`,
              text: 'Your bank transactions CSV has been downloaded'
            },
            errorToast: {
              type: 'error',
              title: `Error`,
              text: 'An error occurred while downloading your bank transactions CSV'
            }
          })

          const formatToYMD = (value) => {
            const d = new Date(value)
            if (Number.isNaN(d.getTime())) return ''

            return d.toISOString().slice(0, 10)
          }

          const startDateFormatted = startDate ? formatToYMD(startDate) : ''
          const endDateFormatted = endDate ? formatToYMD(endDate) : ''

          const blob = new Blob([transactionsCSV], { type: 'text/csv' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `${startDateFormatted}_${endDateFormatted}_PlaidTransactions.csv`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blobUrl)

          resolve(transactionsCSV)
        }
      })
    })
  },
  fetchAllTransactionsForBankFeedAsPDF: async ({ startAtIndex, pageSize, plaidBankAccountId, startDate, endDate }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllTransactionsForBankFeedAsPDF-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          const transactionsPDF = await ospreyAPI({
            service: 'banking',
            endpoint: '/PlaidBankAccount/GetAllTransactionsBySearchCriteriaAsPdf',
            method: 'POST',
            data: {
              startAtIndex,
              pageSize,
              plaidBankAccountId,
              startDate: startDate ?? null,
              endDate: endDate ?? null
            },
            headers: { Accept: '*/*', 'Content-Type': 'application/json' },
            responseType: 'blob',
            successToast: {
              type: 'success',
              title: `Success`,
              text: 'Your bank transactions PDF has been downloaded'
            },
            errorToast: {
              type: 'error',
              title: `Error`,
              text: 'An error occurred while downloading your bank transactions PDF'
            }
          })

          const formatToYMD = (value) => {
            const d = new Date(value)
            if (Number.isNaN(d.getTime())) return ''

            return d.toISOString().slice(0, 10)
          }

          const startDateFormatted = startDate ? formatToYMD(startDate) : ''
          const endDateFormatted = endDate ? formatToYMD(endDate) : ''

          const blob = new Blob([transactionsPDF], { type: 'application/pdf' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `${startDateFormatted}_${endDateFormatted}_PlaidTransactions.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blob)

          resolve(transactionsPDF)
        }
      })
    })
  },
  fetchCurrentBankFeedBalance: async ({ plaidBankAccountId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchCurrentBankFeedBalance-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          const balance = await ospreyAPI({
            service: 'banking',
            endpoint: `/PlaidBankAccount/GetCurrentBalance`,
            method: 'GET',
            data: { plaidBankAccountId }
          })

          set((state) => {
            const updatedBankFeeds = (state.bankFeeds || []).map((feed) =>
              feed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...feed,
                    currentBalance: balance
                  }
                : feed
            )

            const updatedBankFeed =
              state.bankFeed && state.bankFeed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...state.bankFeed,
                    currentBalance: balance
                  }
                : state.bankFeed

            return {
              bankFeeds: updatedBankFeeds,
              bankFeed: updatedBankFeed
            }
          })

          resolve(balance)
        }
      })
    })
  },
  refreshBankFeedTransactions: async ({ plaidBankAccountId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `refreshBankFeedTransactions-${plaidBankAccountId}`,
        delay: 300,
        callback: async () => {
          const response = await ospreyAPI({
            service: 'banking',
            endpoint: `/PlaidBankAccount/RefreshTransactions`,
            method: 'GET',
            data: { plaidBankAccountId }
          })

          const lastSync = new Date().toISOString()

          set((state) => {
            const updatedBankFeeds = (state.bankFeeds || []).map((feed) =>
              feed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...feed,
                    lastSync: lastSync
                  }
                : feed
            )

            const updatedBankFeed =
              state.bankFeed && state.bankFeed.plaidBankAccountId === plaidBankAccountId
                ? {
                    ...state.bankFeed,
                    lastSync: lastSync
                  }
                : state.bankFeed

            return {
              bankFeeds: updatedBankFeeds,
              bankFeed: updatedBankFeed
            }
          })

          resolve(response)
        }
      })
    })
  },
  setCurrentLastSync: async ({ plaidBankAccountId, currentLastSync }) => {
    set((state) => {
      const updateFeed = (feed) =>
        feed.plaidBankAccountId === plaidBankAccountId
          ? {
              ...feed,
              currentLastSync
            }
          : feed

      return {
        bankFeeds: (state.bankFeeds || []).map(updateFeed),
        bankFeed:
          state.bankFeed && state.bankFeed.plaidBankAccountId === plaidBankAccountId
            ? {
                ...state.bankFeed,
                currentLastSync
              }
            : state.bankFeed
      }
    })
  },
  reset: () => set(initialState)
}))
